import React, { useState, useEffect } from 'react';
import { Paperclip, Upload, File, Download, Trash, RefreshCw, AlertCircle, Eye, X } from 'lucide-react';
import claimAttachmentsService from '../../services/claimAttachmentsService';
import FilePreview from './FilePreview';
import { toast } from 'react-toastify';

const styles = {
    section: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '1.5rem',
        marginBottom: '1.5rem',
    },
    header: {
        color: '#0A6EBD',
        fontSize: '1.2rem',
        fontWeight: '600',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    fileList: {
        listStyle: 'none',
        padding: '0',
        margin: '1rem 0',
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '0.75rem',
        borderRadius: '4px',
        backgroundColor: '#f8f9fa',
        marginBottom: '0.5rem',
        justifyContent: 'space-between',
    },
    fileIcon: {
        color: '#F76707',
        marginRight: '0.75rem',
    },
    fileName: {
        flex: '1',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fileActions: {
        display: 'flex',
        gap: '0.5rem',
    },
    actionButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        padding: '0.25rem',
        borderRadius: '4px',
        transition: 'background-color 0.2s',
    },
    downloadButton: {
        color: '#0A6EBD',
    },
    previewButton: {
        color: '#28a745',
    },
    deleteButton: {
        color: '#dc3545',
    },
    uploadSection: {
        marginTop: '1.5rem',
        padding: '1.5rem',
        borderRadius: '8px',
        backgroundColor: '#f8f9fa',
        border: '1px dashed #ced4da',
    },
    uploadTitle: {
        fontSize: '1rem',
        fontWeight: '600',
        marginBottom: '1rem',
    },
    uploadButton: {
        backgroundColor: '#0A6EBD',
        color: 'white',
        border: 'none',
        padding: '0.5rem 1rem',
        borderRadius: '4px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        transition: 'background-color 0.2s',
    },
    submitButton: {
        backgroundColor: '#28a745',
        color: 'white',
        border: 'none',
        padding: '0.5rem 1rem',
        borderRadius: '4px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginTop: '1rem',
    },
    alert: {
        padding: '0.75rem 1.25rem',
        borderRadius: '0.25rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    alertSuccess: {
        backgroundColor: '#d4edda',
        color: '#155724',
        border: '1px solid #c3e6cb',
    },
    alertDanger: {
        backgroundColor: '#f8d7da',
        color: '#721c24',
        border: '1px solid #f5c6cb',
    },
    selectedFiles: {
        marginTop: '1rem',
        padding: '0.5rem',
    },
    filePreview: {
        display: 'flex',
        alignItems: 'center',
        padding: '0.5rem',
        backgroundColor: '#e9ecef',
        borderRadius: '4px',
        marginBottom: '0.5rem',
    },
    removeButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#dc3545',
        marginLeft: 'auto',
    },
    hidden: {
        display: 'none',
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.25rem 0.5rem',
        borderRadius: '0.25rem',
        fontSize: '0.875em',
        fontWeight: '700',
        color: '#fff',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        verticalAlign: 'baseline',
        marginLeft: '0.5rem',
    },
    badgeSuccess: {
        backgroundColor: '#28a745',
    },
    badgeWarning: {
        backgroundColor: '#ffc107',
        color: '#212529',
    },
    modal: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1050,
    },
    modalContent: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '1.5rem',
        width: '400px',
        maxWidth: '90%',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
    },
    modalTitle: {
        color: '#dc3545',
        fontSize: '1.2rem',
        fontWeight: '600',
        margin: 0,
    },
    modalCloseButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '1.5rem',
        color: '#6c757d',
    },
    modalFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.5rem',
        marginTop: '1.5rem',
    },
};

const ClaimAttachments = ({ trackingNumber, onAttachmentsUpdated }) => {
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fileInputRef = React.useRef(null);

    // Charger les pièces jointes au chargement du composant
    useEffect(() => {
        loadAttachments();
    }, [trackingNumber]);

    const loadAttachments = async () => {
        // Vérifier si trackingNumber est valide
        if (!trackingNumber) {
            setError('Numéro de suivi manquant. Impossible de charger les pièces jointes.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const data = await claimAttachmentsService.getAttachments(trackingNumber);
            setAttachments(data);
        } catch (err) {
            console.error('Erreur lors du chargement des pièces jointes:', err);
            setError('Impossible de charger les pièces jointes. Veuillez réessayer plus tard.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setSelectedFiles(files);

        // Réinitialiser les états d'upload
        setUploadSuccess(false);
        setUploadError(null);
    };

    const handleFileRemove = (index) => {
        const newFiles = [...selectedFiles];
        newFiles.splice(index, 1);
        setSelectedFiles(newFiles);
    };

    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    const handleSubmit = async () => {
        if (!trackingNumber) {
            setUploadError('Numéro de suivi manquant. Impossible d\'ajouter des pièces jointes.');
            return;
        }

        if (selectedFiles.length === 0) {
            setUploadError('Veuillez sélectionner au moins un fichier à ajouter.');
            return;
        }

        setUploading(true);
        setUploadError(null);
        setUploadSuccess(false);

        try {
            await claimAttachmentsService.addAttachments(trackingNumber, selectedFiles);

            // Réinitialiser les états
            setSelectedFiles([]);
            fileInputRef.current.value = '';
            setUploadSuccess(true);

            // Recharger les pièces jointes
            await loadAttachments();

            // Notifier le composant parent si besoin
            if (onAttachmentsUpdated) {
                onAttachmentsUpdated();
            }

            toast.success('Pièces jointes ajoutées avec succès');
        } catch (err) {
            console.error('Erreur lors de l\'ajout des pièces jointes:', err);
            setUploadError(err.response?.data?.message || 'Une erreur est survenue lors de l\'ajout des pièces jointes.');
            toast.error('Erreur lors de l\'ajout des pièces jointes');
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = (attachmentId, fileName) => {
        if (!trackingNumber || !attachmentId) {
            console.error('Erreur: trackingNumber et attachmentId sont requis pour le téléchargement');
            toast.error('Impossible de télécharger le fichier');
            return;
        }

        const downloadUrl = claimAttachmentsService.getDownloadUrl(trackingNumber, attachmentId);

        // Créer un lien invisible et le cliquer pour lancer le téléchargement
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePreview = (attachment) => {
        if (!trackingNumber || !attachment || !attachment.id) {
            console.error('Erreur: trackingNumber et attachmentId sont requis pour la prévisualisation');
            toast.error('Impossible de prévisualiser le fichier');
            return;
        }

        if (claimAttachmentsService.isPreviewable(attachment.fileName)) {
            setPreviewAttachment(attachment);
            setIsPreviewOpen(true);
        } else {
            toast.info('Ce type de fichier ne peut pas être prévisualisé');
        }
    };

    const closePreview = () => {
        setIsPreviewOpen(false);
        setPreviewAttachment(null);
    };

    const confirmDelete = (attachment) => {
        if (!trackingNumber || !attachment || !attachment.id) {
            console.error('Erreur: trackingNumber et attachmentId sont requis pour la suppression');
            toast.error('Impossible de supprimer le fichier');
            return;
        }

        setDeleteConfirm(attachment);
    };

    const cancelDelete = () => {
        setDeleteConfirm(null);
    };

    const handleDelete = async () => {
        if (!trackingNumber || !deleteConfirm || !deleteConfirm.id) {
            console.error('Erreur: trackingNumber et attachmentId sont requis pour la suppression');
            toast.error('Impossible de supprimer le fichier');
            setDeleteConfirm(null);
            return;
        }

        setIsDeleting(true);
        try {
            await claimAttachmentsService.deleteAttachment(trackingNumber, deleteConfirm.id);

            // Recharger les pièces jointes
            await loadAttachments();

            // Notifier le composant parent si besoin
            if (onAttachmentsUpdated) {
                onAttachmentsUpdated();
            }

            toast.success('Pièce jointe supprimée avec succès');
        } catch (err) {
            console.error('Erreur lors de la suppression de la pièce jointe:', err);
            toast.error('Erreur lors de la suppression de la pièce jointe');
        } finally {
            setIsDeleting(false);
            setDeleteConfirm(null);
        }
    };

    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();

        switch (extension) {
            case 'pdf':
                return <File size={20} color="#dc3545" />;
            case 'doc':
            case 'docx':
                return <File size={20} color="#2b579a" />;
            case 'jpg':
            case 'jpeg':
            case 'png':
                return <File size={20} color="#fd7e14" />;
            default:
                return <File size={20} />;
        }
    };

    const isFilePreviewable = (fileName) => {
        return claimAttachmentsService.isPreviewable(fileName);
    };

    return (
        <div style={styles.section}>
            <h3 style={styles.header}>
                <Paperclip size={20} />
                Pièces jointes
                {attachments.length > 0 && (
                    <span style={{ ...styles.badge, ...styles.badgeSuccess }}>
                        {attachments.length}
                    </span>
                )}
            </h3>

            {/* Afficher les messages d'erreur/succès */}
            {uploadSuccess && (
                <div style={{ ...styles.alert, ...styles.alertSuccess }}>
                    <RefreshCw size={16} />
                    Les pièces jointes ont été ajoutées avec succès.
                </div>
            )}

            {uploadError && (
                <div style={{ ...styles.alert, ...styles.alertDanger }}>
                    <AlertCircle size={16} />
                    {uploadError}
                </div>
            )}

            {/* Liste des pièces jointes existantes */}
            {loading ? (
                <div className="d-flex justify-content-center my-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            ) : error ? (
                <div style={{ ...styles.alert, ...styles.alertDanger }}>
                    <AlertCircle size={16} />
                    {error}
                </div>
            ) : attachments.length === 0 ? (
                <p className="text-muted">Aucune pièce jointe pour cette réclamation.</p>
            ) : (
                <ul style={styles.fileList}>
                    {attachments.map((attachment) => (
                        <li key={attachment.id} style={styles.fileItem}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {getFileIcon(attachment.fileName)}
                                <span style={styles.fileName} className="ms-2">{attachment.fileName}</span>
                                {isFilePreviewable(attachment.fileName) && (
                                    <span style={{ ...styles.badge, ...styles.badgeWarning }} className="ms-2">
                                        Prévisualisable
                                    </span>
                                )}
                            </div>
                            <div style={styles.fileActions}>
                                {isFilePreviewable(attachment.fileName) && (
                                    <button
                                        style={{ ...styles.actionButton, ...styles.previewButton }}
                                        onClick={() => handlePreview(attachment)}
                                        title="Prévisualiser"
                                    >
                                        <Eye size={18} />
                                    </button>
                                )}
                                <button
                                    style={{ ...styles.actionButton, ...styles.downloadButton }}
                                    onClick={() => handleDownload(attachment.id, attachment.fileName)}
                                    title="Télécharger"
                                >
                                    <Download size={18} />
                                </button>
                                <button
                                    style={{ ...styles.actionButton, ...styles.deleteButton }}
                                    onClick={() => confirmDelete(attachment)}
                                    title="Supprimer"
                                >
                                    <Trash size={18} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {/* Section pour ajouter de nouvelles pièces jointes */}
            <div style={styles.uploadSection}>
                <h4 style={styles.uploadTitle}>Ajouter des pièces jointes</h4>
                <p className="text-muted mb-3">
                    Formats acceptés : PDF, JPG, PNG, DOC, DOCX. Taille maximale : 5 Mo par fichier.
                </p>

                <button
                    type="button"
                    style={styles.uploadButton}
                    onClick={handleUploadClick}
                    disabled={uploading}
                >
                    <Upload size={18} />
                    Sélectionner des fichiers
                </button>

                <input
                    type="file"
                    ref={fileInputRef}
                    style={styles.hidden}
                    onChange={handleFileSelect}
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />

                {/* Afficher les fichiers sélectionnés */}
                {selectedFiles.length > 0 && (
                    <div style={styles.selectedFiles}>
                        <p>Fichiers sélectionnés :</p>
                        {selectedFiles.map((file, index) => (
                            <div key={index} style={styles.filePreview}>
                                {getFileIcon(file.name)}
                                <span className="ms-2">{file.name}</span>
                                <span className="ms-2 text-muted">({(file.size / 1024 / 1024).toFixed(2)} Mo)</span>
                                <button
                                    type="button"
                                    style={styles.removeButton}
                                    onClick={() => handleFileRemove(index)}
                                    title="Supprimer"
                                >
                                    <Trash size={16} />
                                </button>
                            </div>
                        ))}

                        <button
                            type="button"
                            style={styles.submitButton}
                            onClick={handleSubmit}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Envoi en cours...
                                </>
                            ) : (
                                <>
                                    <Upload size={18} />
                                    Envoyer les fichiers
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* Modal de prévisualisation */}
            <FilePreview
                isOpen={isPreviewOpen}
                onClose={closePreview}
                attachment={previewAttachment}
                trackingNumber={trackingNumber}
            />

            {/* Modal de confirmation de suppression */}
            {deleteConfirm && (
                <div style={styles.modal}>
                    <div style={styles.modalContent}>
                        <div style={styles.modalHeader}>
                            <h5 style={styles.modalTitle}>Confirmation de suppression</h5>
                            <button style={styles.modalCloseButton} onClick={cancelDelete}>
                                <X size={20} />
                            </button>
                        </div>
                        <div>
                            <p>Êtes-vous sûr de vouloir supprimer cette pièce jointe ?</p>
                            <p><strong>{deleteConfirm.fileName}</strong></p>
                            <p className="text-danger">Cette action est irréversible.</p>
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                className="btn btn-secondary"
                                onClick={cancelDelete}
                                disabled={isDeleting}
                            >
                                Annuler
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={handleDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Suppression...
                                    </>
                                ) : (
                                    <>
                                        <Trash size={16} className="me-2" />
                                        Supprimer
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClaimAttachments;